import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  passwordPolicyRequirements,
  type AdminUser,
  type PasswordPolicy,
  type UserRole,
} from '@tcg/shared';
import { ApiClientError, adminUsersApi } from '../../lib/api';
import { useSession } from '../../lib/session';
import { formatDateTime } from '../../lib/format';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
} from '../../components/ui';
import { IconUsers } from '../../components/icons';

/**
 * Admin console for the two-tier user model: list/create/delete users, toggle the
 * admin role, and reset passwords. A second card edits the team-wide password
 * policy. New users and resets return a one-time temporary password that the
 * admin must copy and hand to the user out-of-band.
 */
export function Users() {
  const { user: me } = useSession();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** One-time temp password to surface after a create/reset, with whose it is. */
  const [tempPassword, setTempPassword] = useState<{ email: string; value: string } | null>(null);

  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<UserRole>('user');

  const run = useCallback(async (fn: () => Promise<void>, ok?: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (ok) setNotice(ok);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setUsers(await adminUsersApi.list());
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await loadUsers();
      } catch (err) {
        setError(err instanceof ApiClientError ? err.message : 'Failed to load users');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadUsers]);

  const onCreate = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const { user, tempPassword: temp } = await adminUsersApi.create({
        email: newEmail.trim(),
        role: newRole,
      });
      if (temp) setTempPassword({ email: user.email, value: temp });
      setNewEmail('');
      setNewRole('user');
      await loadUsers();
    }, 'User created.');
  };

  const onReset = (u: AdminUser) =>
    run(async () => {
      const { tempPassword: temp } = await adminUsersApi.resetPassword(u.id);
      setTempPassword({ email: u.email, value: temp });
      await loadUsers();
    }, `Password reset for ${u.email}.`);

  const onToggleRole = (u: AdminUser) =>
    run(async () => {
      await adminUsersApi.setRole(u.id, u.isAdmin ? 'user' : 'admin');
      await loadUsers();
    }, `Role updated for ${u.email}.`);

  const onDelete = (u: AdminUser) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    void run(async () => {
      await adminUsersApi.remove(u.id);
      await loadUsers();
    }, `Deleted ${u.email}.`);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16 text-slate-400">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/"
        icon={<IconUsers className="h-5 w-5" />}
        title="Users & security"
        subtitle="Manage who can sign in, their access level, and the password policy."
      />

      {error && <Banner tone="error">{error}</Banner>}
      {notice && <Banner tone="success">{notice}</Banner>}
      {tempPassword && (
        <Banner tone="warn">
          <div className="space-y-1">
            <p>
              Temporary password for <strong>{tempPassword.email}</strong> — copy it now, it won't be
              shown again. They'll be asked to set their own password at first sign-in.
            </p>
            <code className="inline-block rounded bg-white/70 px-2 py-1 font-mono text-sm text-slate-900 ring-1 ring-amber-300">
              {tempPassword.value}
            </code>
          </div>
        </Banner>
      )}

      <Card>
        <CardHeader title="Add user" subtitle="Creates an account with a one-time temporary password." />
        <CardBody>
          <form onSubmit={onCreate} className="flex flex-wrap items-end gap-3">
            <div className="min-w-[16rem] flex-1">
              <Field label="Email">
                <Input
                  type="email"
                  required
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="person@company.com"
                />
              </Field>
            </div>
            <div className="w-40">
              <Field label="Role">
                <Select value={newRole} onChange={(e) => setNewRole(e.target.value as UserRole)}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </Select>
              </Field>
            </div>
            <Button type="submit" disabled={busy}>
              {busy ? <Spinner /> : 'Create user'}
            </Button>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Users" subtitle={`${users.length} account${users.length === 1 ? '' : 's'}`} />
        <CardBody className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">Role</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Password set</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isMe = u.id === me?.id;
                return (
                  <tr key={u.id} className="border-b border-slate-50 last:border-0">
                    <td className="px-5 py-3">
                      <span className="font-medium text-slate-900">{u.email}</span>
                      {isMe && <span className="ml-2 text-xs text-slate-400">(you)</span>}
                    </td>
                    <td className="px-5 py-3">
                      {u.isAdmin ? <Badge tone="green">Admin</Badge> : <Badge tone="slate">User</Badge>}
                    </td>
                    <td className="px-5 py-3">
                      {!u.hasPassword ? (
                        <Badge tone="red">No password</Badge>
                      ) : u.mustChangePassword ? (
                        <Badge tone="amber">Must change</Badge>
                      ) : (
                        <Badge tone="green">Active</Badge>
                      )}
                    </td>
                    <td className="px-5 py-3 text-slate-500">
                      {u.passwordUpdatedAt ? formatDateTime(u.passwordUpdatedAt) : '—'}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-2">
                        <Button variant="secondary" disabled={busy} onClick={() => void onReset(u)}>
                          Reset password
                        </Button>
                        <Button
                          variant="secondary"
                          disabled={busy || isMe}
                          title={isMe ? 'You cannot change your own role.' : undefined}
                          onClick={() => void onToggleRole(u)}
                        >
                          {u.isAdmin ? 'Make user' : 'Make admin'}
                        </Button>
                        <Button
                          variant="danger"
                          disabled={busy || isMe}
                          title={isMe ? 'You cannot delete your own account.' : undefined}
                          onClick={() => onDelete(u)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardBody>
      </Card>

      <PasswordPolicyCard onError={setError} onNotice={setNotice} />
    </div>
  );
}

/** Editor for the team-wide password policy. Self-contained load/save. */
function PasswordPolicyCard({
  onError,
  onNotice,
}: {
  onError: (msg: string | null) => void;
  onNotice: (msg: string | null) => void;
}) {
  const [policy, setPolicy] = useState<PasswordPolicy | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setPolicy(await adminUsersApi.getPasswordPolicy());
      } catch (err) {
        onError(err instanceof ApiClientError ? err.message : 'Failed to load password policy');
      }
    })();
  }, [onError]);

  if (!policy) {
    return (
      <Card>
        <CardHeader title="Password policy" />
        <CardBody>
          <Spinner />
        </CardBody>
      </Card>
    );
  }

  const set = <K extends keyof PasswordPolicy>(key: K, value: PasswordPolicy[K]) =>
    setPolicy((p) => (p ? { ...p, [key]: value } : p));

  const onSave = (e: FormEvent) => {
    e.preventDefault();
    onError(null);
    onNotice(null);
    setBusy(true);
    (async () => {
      try {
        setPolicy(await adminUsersApi.setPasswordPolicy(policy));
        onNotice('Password policy saved.');
      } catch (err) {
        onError(err instanceof ApiClientError ? err.message : 'Failed to save password policy');
      } finally {
        setBusy(false);
      }
    })();
  };

  const toggles: Array<{ key: keyof PasswordPolicy; label: string }> = [
    { key: 'requireUppercase', label: 'Require an uppercase letter' },
    { key: 'requireLowercase', label: 'Require a lowercase letter' },
    { key: 'requireNumber', label: 'Require a number' },
    { key: 'requireSymbol', label: 'Require a symbol' },
  ];

  return (
    <Card>
      <CardHeader
        title="Password policy"
        subtitle="Applies to every password change and to generated temporary passwords."
      />
      <CardBody>
        <form onSubmit={onSave} className="space-y-4">
          <div className="w-48">
            <Field label="Minimum length">
              <Input
                type="number"
                min={8}
                max={128}
                value={policy.minLength}
                onChange={(e) => set('minLength', Number(e.target.value))}
              />
            </Field>
          </div>
          <div className="space-y-2">
            {toggles.map((t) => (
              <label key={t.key} className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-accent-600 focus:ring-accent-500"
                  checked={policy[t.key] as boolean}
                  onChange={(e) => set(t.key, e.target.checked as PasswordPolicy[typeof t.key])}
                />
                {t.label}
              </label>
            ))}
          </div>
          <div className="rounded-lg bg-slate-50 px-4 py-3 text-xs text-slate-500">
            <p className="mb-1 font-medium text-slate-600">Current requirements</p>
            <ul className="list-inside list-disc space-y-0.5">
              {passwordPolicyRequirements(policy).map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>
          <Button type="submit" disabled={busy}>
            {busy ? <Spinner /> : 'Save policy'}
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}
